import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Gauge,
  TrendingUp,
  TrendingDown,
  Fuel,
  Wrench,
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle
} from "lucide-react";
import { cn } from "@/lib/utils";

interface KPIPanelProps {
  data: {
    vehicles?: { total: number; active: number; inactive: number; inMaintenance: number };
    maintenance?: { totalOrders: number; pending: number; completed: number; totalCost: number };
    fuel?: { totalLiters: number; totalCost: number; avgEfficiency: number };
    costs?: { grandTotal: number; totalFuel: number; totalMaintenance: number; totalExpenses: number; byVehicle?: any[] };
  };
}

export function KPIPanel({ data }: KPIPanelProps) {
  const formatCurrency = (value: number) => 
    `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Calculate KPIs
  const availabilityRate = data.vehicles 
    ? ((data.vehicles.active / data.vehicles.total) * 100) || 0
    : 0;

  const preventiveRate = data.maintenance
    ? ((data.maintenance.completed / data.maintenance.totalOrders) * 100) || 0
    : 0;

  const avgCostPerVehicle = data.costs && data.vehicles?.total
    ? data.costs.grandTotal / data.vehicles.total
    : 0;

  const fuelShare = data.costs?.grandTotal
    ? ((data.costs.totalFuel / data.costs.grandTotal) * 100)
    : 0;

  const maintenanceShare = data.costs?.grandTotal
    ? ((data.costs.totalMaintenance / data.costs.grandTotal) * 100)
    : 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Gauge className="h-5 w-5 text-primary" />
          Indicadores-Chave de Desempenho (KPIs)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Operational KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Availability Rate */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Taxa de Disponibilidade</span>
              <Badge variant={availabilityRate >= 85 ? "default" : availabilityRate >= 70 ? "secondary" : "destructive"}>
                {availabilityRate >= 85 ? <CheckCircle className="h-3 w-3 mr-1" /> : <AlertCircle className="h-3 w-3 mr-1" />}
                {availabilityRate.toFixed(0)}%
              </Badge>
            </div>
            <Progress 
              value={availabilityRate} 
              className={cn(
                "h-2",
                availabilityRate >= 85 ? "[&>div]:bg-green-500" : 
                availabilityRate >= 70 ? "[&>div]:bg-yellow-500" : "[&>div]:bg-red-500"
              )}
            />
            <p className="text-xs text-muted-foreground">
              {data.vehicles?.active || 0} de {data.vehicles?.total || 0} veículos operacionais
            </p>
          </div>

          {/* Preventive Rate */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Índice Preventivo</span>
              <Badge variant={preventiveRate >= 75 ? "default" : preventiveRate >= 50 ? "secondary" : "destructive"}>
                <Wrench className="h-3 w-3 mr-1" />
                {preventiveRate.toFixed(0)}%
              </Badge>
            </div>
            <Progress 
              value={preventiveRate} 
              className={cn(
                "h-2",
                preventiveRate >= 75 ? "[&>div]:bg-green-500" : 
                preventiveRate >= 50 ? "[&>div]:bg-yellow-500" : "[&>div]:bg-red-500"
              )}
            />
            <p className="text-xs text-muted-foreground">
              {data.maintenance?.completed || 0} manutenções concluídas
            </p>
          </div>

          {/* Average Fuel Efficiency */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Eficiência Média</span>
              <Badge variant="outline">
                <Fuel className="h-3 w-3 mr-1" />
                {(data.fuel?.avgEfficiency || 0).toFixed(1)} km/L
              </Badge>
            </div>
            <Progress 
              value={Math.min((data.fuel?.avgEfficiency || 0) * 10, 100)} 
              className="h-2 [&>div]:bg-purple-500"
            />
            <p className="text-xs text-muted-foreground">
              {(data.fuel?.totalLiters || 0).toLocaleString('pt-BR')} litros consumidos
            </p>
          </div>

          {/* Cost per Vehicle */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Custo/Veículo</span>
              <Badge variant="outline">
                <DollarSign className="h-3 w-3 mr-1" />
                {formatCurrency(avgCostPerVehicle)}
              </Badge>
            </div>
            <Progress 
              value={50} 
              className="h-2 [&>div]:bg-emerald-500"
            />
            <p className="text-xs text-muted-foreground">
              Média por veículo no período
            </p>
          </div>
        </div>

        <Separator />

        {/* Cost Distribution */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium">Composição de Custos</h4>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Fuel className="h-4 w-4 text-purple-500" />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm">Combustível</span>
                  <span className="text-sm font-medium">{fuelShare.toFixed(1)}%</span>
                </div>
                <Progress value={fuelShare} className="h-2 [&>div]:bg-purple-500" />
              </div>
              <span className="text-sm text-muted-foreground w-28 text-right">
                {formatCurrency(data.costs?.totalFuel || 0)}
              </span>
            </div>

            <div className="flex items-center gap-3">
              <Wrench className="h-4 w-4 text-orange-500" />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm">Manutenção</span>
                  <span className="text-sm font-medium">{maintenanceShare.toFixed(1)}%</span>
                </div>
                <Progress value={maintenanceShare} className="h-2 [&>div]:bg-orange-500" />
              </div>
              <span className="text-sm text-muted-foreground w-28 text-right">
                {formatCurrency(data.costs?.totalMaintenance || 0)}
              </span>
            </div>

            <div className="flex items-center gap-3">
              <DollarSign className="h-4 w-4 text-emerald-500" />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm">Outras Despesas</span>
                  <span className="text-sm font-medium">{(100 - fuelShare - maintenanceShare).toFixed(1)}%</span>
                </div>
                <Progress value={100 - fuelShare - maintenanceShare} className="h-2 [&>div]:bg-emerald-500" />
              </div>
              <span className="text-sm text-muted-foreground w-28 text-right">
                {formatCurrency(data.costs?.totalExpenses || 0)}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
